/**
 * LongTermParamsForm - Parameters for long-term energy benefits analysis.
 *
 * Props:
 *   - params: Object with long-term analysis parameters
 *   - onChange: Callback(updatedParams) when any field changes
 *
 * Includes:
 *   - Time horizon selector (1-20 years)
 *   - What-if parameter sliders (load growth, tariff change)
 *   - New asset addition forms (PV, wind, battery)
 *
 * Note: No "objective" selector — the KPI engine computes parametric
 * projections, not optimization. Objective only applies to the MILP
 * optimizer (day-ahead). No flex load — no backend model exists.
 */

import React, { useState, useEffect } from 'react'
import { Form, Row, Col, Card, Badge } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

const LONG_TERM_DEFAULTS = {
  timeHorizon: '5_years',
  loadGrowthPct: 0,
  tariffChangePct: 0,
  pvAnnualDegradationPct: 0.5,
  addPvSystem: false,
  newPvCapacityKwp: 50,
  addWindTurbine: false,
  newWindCapacityKw: 25,
  addBattery: false,
  newBatteryCapacityKwh: 100,
}

function LongTermParamsForm({ params, onChange }) {
  const { t } = useTranslation()
  const [localParams, setLocalParams] = useState(params || { ...LONG_TERM_DEFAULTS })

  // Sync defaults to parent on mount so review panel has data
  useEffect(() => {
    if (!params && onChange) onChange({ ...LONG_TERM_DEFAULTS })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (field, value) => {
    const updated = { ...localParams, [field]: value }
    setLocalParams(updated)
    if (onChange) onChange(updated)
  }

  return (
    <div className="long-term-params-form">
      {/* Time Horizon */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
            {t('long_term_params.horizon_title')}
          </h6>
          <Form.Select
            value={localParams.timeHorizon}
            onChange={(e) => handleFieldChange('timeHorizon', e.target.value)}
          >
            <option value="1_year">{t('long_term_params.horizon_1y')}</option>
            <option value="5_years">{t('long_term_params.horizon_5y')}</option>
            <option value="10_years">{t('long_term_params.horizon_10y')}</option>
            <option value="20_years">{t('long_term_params.horizon_20y')}</option>
          </Form.Select>
          <Form.Text className="text-muted">
            {t('long_term_params.horizon_help')}
          </Form.Text>
        </Card.Body>
      </Card>

      {/* What-If Parameter Sliders */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
            {t('long_term_params.whatif_title')}
          </h6>

          {/* Load Growth */}
          <Form.Group className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 0 }}>
                {t('long_term_params.load_growth')}
              </Form.Label>
              <Badge bg="secondary">{localParams.loadGrowthPct > 0 ? '+' : ''}{localParams.loadGrowthPct}%</Badge>
            </div>
            <Form.Range
              min={-30}
              max={50}
              step={5}
              value={localParams.loadGrowthPct}
              onChange={(e) => handleFieldChange('loadGrowthPct', parseInt(e.target.value))}
            />
            <div className="d-flex justify-content-between" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
              <span>{t('long_term_params.scale_low')}</span>
              <span>{t('long_term_params.scale_mid')}</span>
              <span>{t('long_term_params.scale_high')}</span>
            </div>
          </Form.Group>

          {/* Tariff Change */}
          <Form.Group className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 0 }}>
                {t('long_term_params.tariff_escalation')}
              </Form.Label>
              <Badge bg="secondary">{localParams.tariffChangePct > 0 ? '+' : ''}{localParams.tariffChangePct}%</Badge>
            </div>
            <Form.Range
              min={-30}
              max={50}
              step={5}
              value={localParams.tariffChangePct}
              onChange={(e) => handleFieldChange('tariffChangePct', parseInt(e.target.value))}
            />
            <div className="d-flex justify-content-between" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
              <span>{t('long_term_params.scale_low')}</span>
              <span>{t('long_term_params.scale_mid')}</span>
              <span>{t('long_term_params.scale_high')}</span>
            </div>
          </Form.Group>

          {/* PV Degradation Rate */}
          <Form.Group className="mb-0">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 0 }}>
                {t('long_term_params.pv_degradation')}
              </Form.Label>
              <Badge bg="secondary">{t('long_term_params.pv_degradation_unit', { value: localParams.pvAnnualDegradationPct })}</Badge>
            </div>
            <Form.Range
              min={0}
              max={2}
              step={0.1}
              value={localParams.pvAnnualDegradationPct}
              onChange={(e) => handleFieldChange('pvAnnualDegradationPct', parseFloat(e.target.value))}
            />
            <div className="d-flex justify-content-between" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
              <span>{t('long_term_params.deg_low')}</span>
              <span>{t('long_term_params.deg_mid')}</span>
              <span>{t('long_term_params.deg_high')}</span>
            </div>
          </Form.Group>
        </Card.Body>
      </Card>

      {/* New Asset Addition */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
            {t('long_term_params.new_asset_title')}
          </h6>

          {/* Add PV System */}
          <Form.Check
            type="switch"
            id="add-pv-system"
            label={t('long_term_params.add_pv')}
            checked={localParams.addPvSystem}
            onChange={(e) => handleFieldChange('addPvSystem', e.target.checked)}
            className="mb-2"
          />
          {localParams.addPvSystem && (
            <Row className="g-3 mb-3 ms-4">
              <Col md={6}>
                <Form.Label style={{ fontSize: 'var(--font-size-sm)' }}>
                  {t('long_term_params.pv_capacity')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={500}
                  step={5}
                  value={localParams.newPvCapacityKwp}
                  onChange={(e) => handleFieldChange('newPvCapacityKwp', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">{t('long_term_params.pv_capacity_help')}</Form.Text>
              </Col>
            </Row>
          )}

          {/* Add Wind Turbine */}
          <Form.Check
            type="switch"
            id="add-wind-turbine"
            label={t('long_term_params.add_wind')}
            checked={localParams.addWindTurbine}
            onChange={(e) => handleFieldChange('addWindTurbine', e.target.checked)}
            className="mb-2"
          />
          {localParams.addWindTurbine && (
            <Row className="g-3 mb-3 ms-4">
              <Col md={6}>
                <Form.Label style={{ fontSize: 'var(--font-size-sm)' }}>
                  {t('long_term_params.wind_capacity')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={100}
                  step={5}
                  value={localParams.newWindCapacityKw}
                  onChange={(e) => handleFieldChange('newWindCapacityKw', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">{t('long_term_params.wind_capacity_help')}</Form.Text>
              </Col>
            </Row>
          )}

          {/* Add Battery */}
          <Form.Check
            type="switch"
            id="lt-add-battery"
            label={t('long_term_params.add_battery')}
            checked={localParams.addBattery}
            onChange={(e) => handleFieldChange('addBattery', e.target.checked)}
            className="mb-2"
          />
          {localParams.addBattery && (
            <Row className="g-3 ms-4">
              <Col md={6}>
                <Form.Label style={{ fontSize: 'var(--font-size-sm)' }}>
                  {t('long_term_params.batt_capacity')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={10}
                  max={1000}
                  step={10}
                  value={localParams.newBatteryCapacityKwh}
                  onChange={(e) => handleFieldChange('newBatteryCapacityKwh', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">{t('long_term_params.batt_capacity_help')}</Form.Text>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default LongTermParamsForm
